import React, {useEffect, useState} from "react";
import {
    Accordion, AccordionDetails,
    AccordionSummary, AccordionSummaryProps,
    Alert,
    Box, Button,
    Container,
    Divider, FormControlLabel,
    Grid,
    IconButton, MenuItem, Select, SvgIcon, Switch, ToggleButton, Tooltip,
    Typography,
} from "@mui/material";
import Instruction from "./components/Instruction";
import GenerationSettings from "./components/GenerationSettings";
import useModel, {API_ERROR_TYPES, ErrorMessage, Message, TOKEN_SIZE_LIMIT_ERROR_MESSAGE} from "./providers/useModel";
import OutputBox from "./components/OutputBox";
import InstructionSuggestions from "./components/InstructionSuggestions";
import SymblAppBar from "./components/SymblAppBar";
import Transcription from "./components/Transcription";
import {GradientButton, GradientProgressBar, GradientText} from "./components/GradientComponents";
import MediumLighteningIcon from "./icons/MediumLighteningIcon";
import {ExpandMore as ExpandMoreIcon} from "@mui/icons-material";
import MediumSquare from "./icons/MediumSquare";
import MediumArrowLineRightIcon from "./icons/MediumArrowLineRightIcon";
import MediumArrowLineLeftIcon from "./icons/MediumArrowLineLeftIcon";
import {enableSystemPrompt, showGenerationParamsByDefault} from "./config";
import Chat from "./components/Chat";
import {useAuth0} from "@auth0/auth0-react";
import {styled} from "@mui/material/styles";
import LargePlusIcon from "./icons/LargePlusIcon";
import ChatsIcon from "./icons/ChatsIcon";
import NewChatPage from "./components/NewChatPage";
import FlipMove from 'react-flip-move';
import SystemPrompt from "./SystemPrompt";

// interface IFormattedLine {
//     speaker: string;
//     message: string;
// }


const CustomAccordionSummary = styled((props: AccordionSummaryProps) => (
    <AccordionSummary
        expandIcon={<ExpandMoreIcon/>}
        {...props}
    />
))(({ theme }) => ({
    // backgroundColor:
    //     theme.palette.mode === 'dark'
    //         ? 'rgba(255, 255, 255, .05)'
    //         : 'rgba(0, 0, 0, .03)',
    // flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-content': {
        maxWidth: '100%',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1)

    },
    '& .MuiAccordionSummary-root': {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
        marginBottom: "8px",
        marginTop: "8px"
    },
}));

const Playground: React.FC = () => {
    // const [transcriptFile, setTranscriptFile] = useState<File | null>(null);
    // const [formattedTranscript, setFormattedTranscript] = useState<IFormattedLine[]>([]);
    // const [speakerNames, setSpeakerNames] = useState<Set<string>>(new Set());
    const [instruction, setInstruction] = useState<string>("");
    const [transcript, setTranscript] = useState<string>("");
    const [generatedText, setGeneratedText] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [apiErrorMessage, setAPIErrorMessage] = useState<ErrorMessage>();
    const [generationParamsVisible, setGenerationParamsVisible] = useState<boolean>(showGenerationParamsByDefault);
    const [selectedModelType, setSelectedModelType] = useState<string>("chat");
    const [showSamplePrompts, setShowSamplePrompts] = useState<boolean>(true);
    const systemPromptChatRef = React.useRef<HTMLDivElement>(null);
    const chatListRef = React.useRef<HTMLDivElement>(null);

    const {user} = useAuth0();

    const {
        isGenerating,
        isGeneratingSuggestions,
        generationParamsState,
        setGenerationParams,
        generateStreaming,
        suggestions,
        generateSuggestions,
        stopGeneration,
        submitRating,
        resetSuggestions,
        generateChatStreaming,
        createNewUserChat,
        activeUserChatIndex,
        changeActiveUserChat,
        deselectActiveUserChat,
        currentSystemPrompt,
        updateCurrentSystemPrompt,
        userChatsHistory
    } = useModel(undefined, (err: ErrorMessage) => {
        setAPIErrorMessage(err);
    });


    useEffect(() => {
        if (transcript) {
            setInstruction("")
            setErrorMessage("")
            setGeneratedText("")
        }
    }, [transcript]);



    const generateText = async (instruction: string, transcript: string) => {
        if (!instruction) {
            setErrorMessage("Please enter an instruction.")
            return;
        }
        if (instruction.length <= 5) {
            setErrorMessage("Instruction is too short.")
            return;
        }
        setErrorMessage("");
        setInstruction(instruction);
        setGeneratedText("");

        let _text = ''
        for await (const text of generateStreaming(`${instruction}\n${transcript}`)) {
            // _text = text.replaceAll('\n', '<br/>');
            setGeneratedText(text);
        }
        // let prompt = `${instruction}\n${transcript}`.trim();
        // if (!prompt.endsWith('.') && !prompt.endsWith('?') && !prompt.endsWith('!')) {
        //     prompt += '.'
        // }
        // await generateStreamingWS(prompt, ({text}) => {
        //     // _text = text.replaceAll('\n', '<br/>');
        //     setGeneratedText(text);
        // }, ({message}) => {
        //     setErrorMessage(message);
        // });
        return _text
    };

    const stopGenerating = () => {
        stopGeneration();
    }

    // const handleChange = (setter: React.Dispatch<React.SetStateAction<string>>) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    //     setter(e.target.value);
    // };
    //
    // const tokenCount = prompt.length + generatedText.length;

    // const moveUp = (item, evt) => {
    //     const listBottom = chatListRef.current?.offsetTop + chatListRef.current?.clientHeight;
    //     const itemTop = evt.target.offsetTop - listBottom;
    //     const { top, bottom, transition } = this.state;
    //     transition.item = item;
    //     transition.startTop = itemTop;
    //     transition.startAnim = false;
    //
    //     this.setState({
    //         top: [...top, item],
    //         bottom: bottom.filter(x => x !== item),
    //         transition,
    //     })
    //     setTimeout(() => this.resetState(), 500);
    // }

    // const resetState = () => {
    //     const { transition } = this.state;
    //     transition.startAnim = true;
    //     this.setState({transition});
    // }

    const commonUserChatStyles = {borderRadius: 8, width: '100%', padding: "12px", marginBottom: "12px"};
    const activeUserChatStyles = {...commonUserChatStyles, border: "1px solid #DB3B5C"};
    return (
        <div style={{height: "100vh", display: 'flex', flexDirection: 'column'}}>
            {isGenerating && (<GradientProgressBar/>)}
            <SymblAppBar/>
            <Container component="main"
                       maxWidth={false}
                       sx={{mt: 2, mb: 2, ml: 2, mr: 2}}
                       style={{
                           flexGrow: 1, display: 'flex',
                           flexDirection: 'column', padding: 0,
                           marginTop: 0,
                           marginLeft: 0,
                           marginBottom: "24px"
                       }}>
                <Box
                    sx={{
                        paddingLeft: 0,
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: "center",
                        justifyContent: "center",
                        marginLeft: 4,
                        marginRight: 4
                    }}
                >
                    <Grid container spacing={2} style={{
                        justifyContent: 'center',
                        flexGrow: 1,
                        marginTop: 0
                    }}>
                        {selectedModelType === 'instruct' && (
                            <Grid item xs={12} md={4}
                                  sx={{
                                      mt: 2,
                                      ml: 1,
                                      mr: 1
                                  }}
                                  style={{
                                      background: '#FFFFFF',
                                      boxShadow: '0px 12px 16px -4px #0F1A2A14',
                                      borderRadius: 16,
                                      paddingLeft: 0,
                                      paddingTop: 0
                                  }}>

                                <Grid container>
                                    <Grid item xs={12}>
                                        <Box sx={{
                                            mt: 2,
                                            display: 'flex',
                                            width: '100%'
                                        }}>
                                            <Transcription disabled={isGenerating}
                                                           transcript={transcript} transcriptChanged={(transcript) => {
                                                setTranscript(transcript);
                                                setGeneratedText("");
                                                setInstruction("");
                                                resetSuggestions();
                                            }}/>
                                        </Box>
                                    </Grid>
                                </Grid>

                                {/*<TranscriptUploader*/}
                                {/*    formattedTranscript={formattedTranscript}*/}
                                {/*    speakerNames={speakerNames}*/}
                                {/*    handleFileChange={handleFileChange}*/}
                                {/*    handleSpeakerNameChange={handleSpeakerNameChange}*/}
                                {/*/>*/}
                            </Grid>)}
                        <Grid item xs={12} md={selectedModelType === 'instruct' ? 7.5 : 12}
                              id={"middle"}
                              sx={{
                                  mt: 2
                              }}
                              style={{
                                  background: '#FFFFFFAA',
                                  boxShadow: '0px 12px 16px -4px #0F1A2A14',
                                  borderRadius: 16,
                                  paddingTop: 0,
                                  height: 'calc(100% - 16px)',
                                  paddingLeft: 0
                              }}>
                            <Grid container style={{
                                height: '100%',
                                background: "#F6F8FC",
                                marginRight: "2px",
                                borderRadius: "20px"
                            }}>
                                <Grid xs={2.5} md={2.5} style={{padding: '8px', borderRight: "1px solid #E2E8F0"}} ref={chatListRef}>
                                    <GradientButton style={{borderRadius: 999, width: '100%'}}
                                        startIcon={<LargePlusIcon/>}
                                        onClick={() => {
                                            setShowSamplePrompts(true);
                                            deselectActiveUserChat();
                                        }
                                    }
                                       disabled={isGenerating}
                                        >
                                        <GradientText variant={"body2"}>New Chat</GradientText>
                                    </GradientButton>
                                    <Divider variant="fullWidth" style={{marginTop: "24px", marginBottom: "12px"}} />
                                    <FlipMove typeName={null}>
                                    {
                                        userChatsHistory.map(({activeSystemPrompt: systemPrompt, userChats, id}, index) => (<GradientButton key={id}
                                                                                           style={activeUserChatIndex === index ? activeUserChatStyles: commonUserChatStyles}
                                                                                           startIcon={<ChatsIcon/>}
                                                                                           disabled={isGenerating}
                                                                                           onClick={() => {
                                                                                               changeActiveUserChat(index);
                                                                                               setShowSamplePrompts(false);
                                                                                           }}
                                        >
                                            <Typography style={{width: "90%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "left"}}>
                                                {!!systemPrompt ? systemPrompt : userChats?.length > 0  && userChats[0].chats?.length > 0 ? userChats[0].chats[0].text : "New Chat"}
                                            </Typography>
                                        </GradientButton>))
                                    }
                                    </FlipMove>
                                </Grid>
                                <Grid item xs={12} md={generationParamsVisible && !showSamplePrompts ? 7.5 : 9.5}>
                                    {
                                        showSamplePrompts ? <NewChatPage
                                                onStartNewChatClicked={() => {
                                                setShowSamplePrompts(false);
                                                createNewUserChat("");
                                            }} onSamplePromptClick={(prompt) => {
                                                setShowSamplePrompts(false);
                                                createNewUserChat(prompt.text);
                                            }} />
                                        : selectedModelType === 'chat' && (
                                        <div style={{
                                                  height: '100%',
                                                  display: 'flex',
                                                  flexDirection: 'column',
                                                  justifyContent: 'space-between',
                                                  alignItems: 'flex-start',
                                                  alignContent: 'flex-start'
                                              }}>
                                            <Grid container style={{display: "flex", alignItems: "stretch", position: "relative"}}>
                                                {
                                                    apiErrorMessage?.type === API_ERROR_TYPES.INVALID_INPUT &&
                                                    <Grid item xs={9} md={generationParamsVisible ? 12 : 10}>
                                                        <Alert severity="error" style={{
                                                            backgroundColor: '#FFF5F5',
                                                            borderWidth: '1px 1px 4px 1px',
                                                            borderStyle: 'solid',
                                                            borderColor: '#FED7D7',
                                                            borderRadius: 8,
                                                            margin: "4px 4px 8px 4px"
                                                        }}>
                                                            {"Internal server error"}
                                                        </Alert>
                                                    </Grid>
                                                }
                                            {enableSystemPrompt ? (
                                                <SystemPrompt
                                                    generationParamsVisible={generationParamsVisible}
                                                    currentSystemPrompt={currentSystemPrompt}
                                                    updateCurrentSystemPrompt={updateCurrentSystemPrompt}
                                                    isGenerating={isGenerating}/>) : null}
                                            {!generationParamsVisible && (<Grid container spacing={1} style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    marginTop: '4px',
                                                    marginRight: '22px',
                                                    position: "absolute",
                                                    top: "2px",
                                                    right: 0,
                                                    width: "220px",
                                                    maxWidth: "200px"
                                                }}>
                                                    <Grid item xs={10} md={10} style={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-end',
                                                        alignItems: 'flex-start',
                                                        marginTop: "2px"
                                                    }}>
                                                        <Typography variant={"body2"}>
                                                            Generation Parameters
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={2} md={2} style={{
                                                        display: 'flex',
                                                        justifyContent: 'flex-end',
                                                        alignItems: 'flex-start',
                                                        paddingTop: "4px"
                                                    }}>
                                                        <IconButton onClick={() => {
                                                            setGenerationParamsVisible(true)
                                                        }}>
                                                            <MediumArrowLineLeftIcon/>
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>)}
                                            </Grid>
                                            <div style={{flex: 1, width: "100%"}}>
                                                <Chat userPhotoUrl={user?.picture}
                                                      userDisplayName={user?.name}
                                                      isGenerating={isGenerating}
                                                      messageSubmitted={async (message) => {
                                                          const newMessage: Message = {
                                                              role: 'human',
                                                              text: message
                                                          }
                                                          await generateChatStreaming(newMessage);
                                                      }}
                                                      userChatHistory={userChatsHistory[activeUserChatIndex]}
                                                      stopGenerating={stopGenerating}
                                                />
                                            </div>
                                        </div>
                                    )
                                    }
                                    {selectedModelType === 'instruct' && (
                                        <Grid container
                                              style={{
                                                  paddingTop: 16,
                                                  paddingRight: 16
                                              }}
                                              direction="row"
                                              justifyContent="center"
                                              alignItems="center"
                                              spacing={2}>
                                            {
                                                errorMessage && (
                                                    <Alert severity="error" sx={{ml: 2, mr: 2, mt: 2}} style={{
                                                        width: '100%',
                                                        backgroundColor: '#FFF5F5',
                                                        borderWidth: '1px 1px 4px 1px',
                                                        borderStyle: 'solid',
                                                        borderColor: '#FED7D7',
                                                        borderRadius: 8
                                                    }}>{errorMessage}</Alert>)
                                            }
                                            <Grid style={{padding: 16, paddingBottom: 0}} item xs={12} md={12}
                                                  sx={{mb: 1}}>
                                                <Grid container>
                                                    {enableSystemPrompt ? (
                                                        <Grid item xs={12} md={12} style={{marginBottom: 16}}>
                                                            <Accordion style={{
                                                                boxShadow: '0px 0px 0px 0px #0F1A2A14',
                                                                border: 'solid #E2E8F0',
                                                                borderWidth: '0px 0px 1px 0px',
                                                                width: '100%',
                                                                padding: 0
                                                            }}>
                                                                <AccordionSummary style={{padding: 0}}>
                                                                    <Typography variant={"body2"}
                                                                                style={{fontWeight: 600}}
                                                                                sx={{mb: 1}}>System Prompt</Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails style={{padding: 0}}>
                                                                    <Instruction instruction={currentSystemPrompt}
                                                                                 instructionSubmitted={() => {
                                                                                 }}
                                                                                 disableIcon={true}
                                                                                 minRows={3}
                                                                                 placeholderText={"Provide any additional context here."}
                                                                                 setInstruction={updateCurrentSystemPrompt}
                                                                                 isGenerating={isGenerating}/>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        </Grid>) : null}
                                                    <Grid item xs={6} md={8}>
                                                        {!generationParamsVisible && (
                                                            <Grid container spacing={1} style={{
                                                                display: 'flex',
                                                                justifyContent: 'flex-end'
                                                            }}>
                                                                <Grid item xs={11} md={11} style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'flex-end',
                                                                    alignItems: 'center'
                                                                }}>
                                                                    <Typography variant={"body2"}>
                                                                        Generation Parameters
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={1} md={1} style={{
                                                                    display: 'flex',
                                                                    justifyContent: 'flex-end',
                                                                    alignItems: 'center'
                                                                }}>
                                                                    <IconButton onClick={() => {
                                                                        setGenerationParamsVisible(true)
                                                                    }}>
                                                                        <MediumArrowLineLeftIcon/>
                                                                    </IconButton>
                                                                </Grid>
                                                            </Grid>)}
                                                    </Grid>
                                                </Grid>
                                                <Typography variant={"body2"} style={{fontWeight: 600}}
                                                            sx={{mb: 1}}>Instruction</Typography>
                                                <Instruction
                                                    instruction={instruction}
                                                    setInstruction={setInstruction}
                                                    instructionSubmitted={(_instruction) => generateText(_instruction, transcript)}
                                                    isGenerating={isGenerating}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={12} style={{padding: 16, paddingTop: 0}}>
                                                <InstructionSuggestions suggestions={suggestions}
                                                                        isGeneratingSuggestions={isGeneratingSuggestions}
                                                                        suggestionSelected={setInstruction}
                                                                        generateSuggestions={() => {
                                                                            generateSuggestions(transcript).then(() => {
                                                                            }).catch((e) => {
                                                                                setErrorMessage(`${e.message}`);
                                                                            })
                                                                        }}
                                                ></InstructionSuggestions>
                                            </Grid>
                                            <Grid item xs={12} md={12} style={{padding: 16}}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12} md={6}>
                                                        <GradientButton gradientBackground={true}
                                                            // disabled={isGenerating}
                                                                        startIcon={
                                                                            isGenerating ?
                                                                                <MediumSquare/> :
                                                                                <MediumLighteningIcon/>
                                                                        }
                                                                        style={{
                                                                            width: '100%',
                                                                            borderRadius: 999,
                                                                            background: isGenerating ? 'linear-gradient(270deg, #617187 0%, #27364B 100%)' : undefined
                                                                        }}
                                                                        onClick={() => {
                                                                            if (!isGenerating) {
                                                                                generateText(instruction, transcript)
                                                                            } else {
                                                                                stopGenerating();
                                                                            }
                                                                        }}
                                                        >
                                                            <Typography variant={"body2"}>{
                                                                isGenerating ? "Stop Generating"
                                                                    : "Generate Output"
                                                            }</Typography>
                                                        </GradientButton>
                                                    </Grid>
                                                    <Grid item xs={12} md={6}>
                                                        <GradientButton
                                                            onClick={() => {
                                                                setGeneratedText('');
                                                                setInstruction('');
                                                            }}
                                                            disabled={isGenerating}
                                                            style={{
                                                                width: '100%',
                                                                borderRadius: 999
                                                            }}>
                                                            <GradientText variant={"body2"}
                                                                          disabled={isGenerating}>
                                                                Clear
                                                            </GradientText>
                                                        </GradientButton>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12} md={12}>
                                                <Divider variant="fullWidth"/>
                                            </Grid>
                                            <Grid item xs={12} md={12} style={{padding: 16}}>
                                                <OutputBox
                                                    isGenerating={isGenerating}
                                                    stopGenerating={stopGenerating}
                                                    outputText={generatedText}
                                                    ratingSubmitted={(rating) => {
                                                        submitRating(rating, instruction, transcript, generatedText);
                                                    }}
                                                />
                                            </Grid>
                                            {/*<Grid item>*/}
                                            {/*    <Typography variant="subtitle1">{`Token count: ${tokenCount}`}</Typography>*/}
                                            {/*</Grid>*/}

                                        </Grid>)}
                                </Grid>
                                {(generationParamsVisible && !showSamplePrompts) && (<Grid item id={"right"} xs={12} md={2} style={{
                                    background: '#F6F8FC',
                                    borderLeft: '1px solid #E2E8F0',
                                    borderTopRightRadius: 16,
                                    borderBottomRightRadius: 16,
                                }}>
                                    <Box sx={{ml: 2, mr: 2}} style={{
                                        display: 'flex', flexDirection: 'column'
                                    }}>
                                        {/*{enableModelSelection && (*/}
                                        {/*    <Grid container style={{paddingTop: 5}}>*/}
                                        {/*        <Grid item xs={12}*/}
                                        {/*              style={{display: 'flex', alignItems: 'center', paddingRight: 16}}>*/}
                                        {/*            <SelectModel models={['nebula-instruct-large-1',*/}
                                        {/*                'nebula-chat-large-1']}/>*/}
                                        {/*        </Grid>*/}
                                        {/*    </Grid>*/}
                                        {/*)}*/}
                                        <Grid container style={{
                                            paddingTop: 5
                                        }}>
                                            <Grid item xs={10}>
                                                <Typography style={{paddingTop: 12, paddingBottom: 16}} variant="body2">Generation
                                                    Parameters</Typography>
                                            </Grid>
                                            <Grid item xs={2} style={{display: 'flex', alignItems: 'center'}}>
                                                <IconButton onClick={() => {
                                                    setGenerationParamsVisible(false)
                                                }}>
                                                    <MediumArrowLineRightIcon/>
                                                </IconButton>

                                            </Grid>
                                        </Grid>

                                        <GenerationSettings
                                            generationParams={generationParamsState}
                                            setGenerationParams={setGenerationParams}
                                        />
                                    </Box>
                                </Grid>)}

                            </Grid>

                        </Grid>
                    </Grid>

                </Box>
            </Container>
        </div>
    )
        ;
};

export default Playground;
